import { createToken, measureServerOffset, msLeftInWindow, windowAt } from '@iris/qr-token';
import type { QrSeed } from '@iris/shared';

/**
 * The QR seed kept on the device (T3 §3.5, NFR-06): the per-user secret and the measured offset to server time, so
 * My QR draws valid tokens without network. One seed per device; sign-out deletes it.
 */
const KEY = 'iris-qr-seed';

export interface SeedStorage {
  getItem(key: string): Promise<string | null>;
  setItem(key: string, value: string): Promise<void>;
  removeItem(key: string): Promise<void>;
}

export interface StoredSeed {
  userId: string;
  /** 32 bytes, base64url, as the API sends it */
  secret: string;
  /** server time − device time, measured at the last successful fetch (FR-QR-04) */
  offsetMs: number;
  /** shown under the QR on an offline cold start, when /me can't load (FR-QR-02, FR-WAL-06) */
  memberCode: string;
  firstName: string | null;
}

export type SeedProfile = Pick<StoredSeed, 'memberCode' | 'firstName'>;

/**
 * The device's seed. With `userId`, only that account's; without (offline cold start, no /me yet), whatever this
 * device holds — sign-out deletes it, so it belongs to the account still signed in here.
 */
export async function readSeed(storage: SeedStorage, userId?: string): Promise<StoredSeed | null> {
  try {
    const seed = JSON.parse((await storage.getItem(KEY)) ?? 'null') as StoredSeed | null;
    if (!seed || typeof seed.secret !== 'string' || typeof seed.memberCode !== 'string') return null;
    return userId === undefined || seed.userId === userId ? seed : null;
  } catch {
    return null;
  }
}

/** GET /me/qr-seed, timing the round trip to measure the device's clock offset. */
export async function fetchSeed(
  load: () => Promise<QrSeed>,
  profile: SeedProfile,
  storage: SeedStorage,
  deviceNow: () => number,
): Promise<StoredSeed> {
  const sentAt = deviceNow();
  const data = await load();
  const receivedAt = deviceNow();
  const seed: StoredSeed = {
    userId: data.userId,
    secret: data.secret,
    offsetMs: measureServerOffset(data.serverTime, sentAt, receivedAt),
    memberCode: profile.memberCode,
    firstName: profile.firstName,
  };
  await storage.setItem(KEY, JSON.stringify(seed));
  return seed;
}

export async function clearSeed(storage: SeedStorage): Promise<void> {
  await storage.removeItem(KEY);
}

export function decodeSecret(base64url: string): Uint8Array {
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
  const binary = atob(base64 + '='.repeat((4 - (base64.length % 4)) % 4));
  return Uint8Array.from(binary, (char) => char.charCodeAt(0));
}

/** What My QR shows at a device instant: the token of the current server window and the time left in it. */
export async function frameAt(
  seed: StoredSeed,
  deviceNow: number,
): Promise<{ token: string; window: number; msLeft: number }> {
  const window = windowAt(deviceNow, seed.offsetMs);
  return {
    token: await createToken('IR1', seed.userId, decodeSecret(seed.secret), window),
    window,
    msLeft: msLeftInWindow(deviceNow, seed.offsetMs),
  };
}

/**
 * The voucher's rotating QR (FR-REW-04, T3 §4.3): IRV1 with the voucher's own secret, on the server's 30-s window
 * (the device's measured offset), drawn offline like My QR.
 */
export async function voucherFrameAt(
  voucherId: string,
  secret: string,
  offsetMs: number,
  deviceNow: number,
): Promise<{ token: string; msLeft: number }> {
  return {
    token: await createToken('IRV1', voucherId, decodeSecret(secret), windowAt(deviceNow, offsetMs)),
    msLeft: msLeftInWindow(deviceNow, offsetMs),
  };
}
